import { useEffect } from "react";
import { Helmet } from "react-helmet-async";

const SITE_URL = "https://codeplanning.co.nz";
const SITE_NAME = "Code Planning Limited";
const DEFAULT_IMAGE = `${SITE_URL}/og-default.jpg`;
const DEFAULT_DESCRIPTION =
  "Code Planning Limited is a senior resource consent planning firm based on Auckland's North Shore. Residential, commercial, subdivision, heritage, terraced housing, coastal and cross lease conversions — resource consents, simplified.";

/**
 * Reusable SEO component for setting per-page meta tags, Open Graph,
 * Twitter Card and Schema.org JSON-LD structured data.
 *
 * Uses react-helmet-async's prop-driven API (works reliably with React 19).
 */
export default function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  path = "/",
  image,
  type = "website",
  keywords,
  jsonLd,
  noindex = false,
}) {
  // Remove the per-route SEO bootstrap tags injected by index.html once
  // react-helmet takes over — prevents duplicate <title>, <meta description>
  // and <meta property="og:title"/og:description> tags in the rendered DOM.
  useEffect(() => {
    document
      .querySelectorAll(
        "[data-bootstrap-title], [data-bootstrap-desc], [data-bootstrap-og]"
      )
      .forEach((el) => el.parentNode && el.parentNode.removeChild(el));
  }, []);
  const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
  const canonical = `${SITE_URL}${path}`;
  const resolvedImage = image
    ? image.startsWith("http")
      ? image
      : `${SITE_URL}${image}`
    : DEFAULT_IMAGE;
  const jsonLdArray = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  const meta = [
    { name: "description", content: description },
    { property: "og:type", content: type },
    { property: "og:site_name", content: SITE_NAME },
    { property: "og:title", content: fullTitle },
    { property: "og:description", content: description },
    { property: "og:url", content: canonical },
    { property: "og:image", content: resolvedImage },
    { property: "og:locale", content: "en_NZ" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: fullTitle },
    { name: "twitter:description", content: description },
    { name: "twitter:image", content: resolvedImage },
  ];
  if (keywords) meta.push({ name: "keywords", content: keywords });
  if (noindex) meta.push({ name: "robots", content: "noindex, nofollow" });

  const link = [{ rel: "canonical", href: canonical }];

  const script = jsonLdArray.map((schema) => ({
    type: "application/ld+json",
    innerHTML: JSON.stringify(schema),
  }));

  return (
    <Helmet
      title={fullTitle}
      meta={meta}
      link={link}
      script={script}
    />
  );
}

/**
 * Shared Schema.org organisation/local business object so all pages
 * can reference the same canonical entity.
 */
export const ORGANISATION_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  "@id": `${SITE_URL}/#organization`,
  name: SITE_NAME,
  alternateName: "Code Planning",
  url: SITE_URL,
  logo: `${SITE_URL}/logo.png`,
  image: DEFAULT_IMAGE,
  description: DEFAULT_DESCRIPTION,
  email: "mills@codeplanning.co.nz",
  telephone: "+64-21-110-6525",
  priceRange: "$$",
  areaServed: [
    { "@type": "City", name: "Auckland" },
    { "@type": "AdministrativeArea", name: "Auckland Region" },
    { "@type": "Country", name: "New Zealand" },
  ],
  address: {
    "@type": "PostalAddress",
    addressLocality: "Auckland",
    addressRegion: "North Shore",
    addressCountry: "NZ",
  },
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "5.0",
    reviewCount: "80",
    bestRating: "5",
    worstRating: "1",
  },
  sameAs: [],
};

export { SITE_URL, SITE_NAME, DEFAULT_DESCRIPTION };
